// Where the editor runs and what it may do there. One build, two hosts: the browser
// (editor.pergamenos.com) and the desktop shell (`desktop/`, Tauri). Capabilities, never forks:
// `local` = the desktop's checkout, pipeline runs, Blender, file watching and offline store;
// `generate` = the server's flag (Blender + pipeline on the host serving the editor).
import { create } from "zustand";

export interface DesktopInfo {
  version: string;
  checkout: string | null;
  server: string;
  /** Connect base URL (`…/api`, or a bare loopback server). */
  api: string;
  /** Published asset store the desktop mirrors. */
  assets: string;
  blender: string;
  devToken: boolean;
}

export interface Capabilities {
  /** The desktop shell with a checkout set. */
  local: boolean;
  /** Generation on the server (flag `generate`). */
  serverGenerate: boolean;
  desktop: DesktopInfo | null;
}

export const isDesktop = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export const useCapabilities = create<Capabilities>()(() => ({ local: false, serverGenerate: false, desktop: null }));

/** Generation controls work: on the desktop (Blender here) or with the server's flag. */
export function canGenerate(c: Capabilities = useCapabilities.getState()): boolean {
  return c.local || c.serverGenerate;
}

type Invoke = <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
let invokeFn: Invoke | null = null;

/** A command of the desktop shell (`desktop/src-tauri/src/lib.rs`). */
export async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  if (!isDesktop) throw new Error(`${cmd}: not the desktop app`);
  invokeFn ??= (await import("@tauri-apps/api/core")).invoke as Invoke;
  return invokeFn<T>(cmd, args);
}

let fetchFn: typeof fetch | undefined;

/** Reads the host's settings; the API client and asset URLs depend on them (call before anything else). */
export async function initPlatform(): Promise<void> {
  if (!isDesktop) return;
  // The editor server sends no CORS headers: the desktop calls it through Rust, not the page.
  fetchFn = (await import("@tauri-apps/plugin-http")).fetch as typeof fetch;
  setDesktop(await invoke<DesktopInfo>("desktop_info"));
}

export function setDesktop(info: DesktopInfo): void {
  useCapabilities.setState({ desktop: info, local: !!info.checkout });
}

export function apiBase(): string {
  const d = useCapabilities.getState().desktop;
  if (d) return d.api;
  return import.meta.env.VITE_API_BASE ?? new URL("api", document.baseURI).href;
}

export function apiFetch(): typeof fetch | undefined {
  return fetchFn;
}

/** A custom scheme of the shell as the webview addresses it (`https://<scheme>.localhost/` on Windows). */
function scheme(name: string): string {
  return /Windows/.test(navigator.userAgent) ? `https://${name}.localhost/` : `${name}://localhost/`;
}

/**
 * Base of `<sha256>.<ext>` store files: the site's `/assets/`, or on the desktop the shell's
 * scheme (local build's store, offline mirror, then the published store).
 */
export function assetBase(): string {
  return isDesktop ? scheme("pgasset") : new URL("assets/", location.origin + "/").href;
}

/** URL of a checkout file (`art/…`, `content/…`) on the desktop. */
export function localUrl(path: string): string {
  return scheme("pglocal") + path.split("/").map(encodeURIComponent).join("/");
}
